import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, AlertTriangle, FileText, Brain, Zap } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface QualityScore {
  overall: number;
  clarity: number;
  accuracy: number;
  completeness: number;
  relevance: number;
  structure: number;
  suggestions: string[];
  issues: string[];
}

export default function ContentQualityScorer() {
  const { toast } = useToast();
  const { session } = useAuth();
  const [content, setContent] = useState("");
  const [contentType, setContentType] = useState("mcq");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [score, setScore] = useState<QualityScore | null>(null);

  const { data: recentAnalyses, refetch: refetchAnalyses } = useQuery({
    queryKey: ['recent-quality-analyses'],
    queryFn: async () => {
      const { data } = await supabase
        .from('content_quality_analyses')
        .select('*')
        .order('analyzed_at', { ascending: false })
        .limit(10);
      return data || [];
    },
  });

  const analyzeContent = async () => {
    if (!content.trim()) {
      toast({
        title: "No Content",
        description: "Please enter content to analyze.",
        variant: "destructive"
      });
      return;
    }

    setIsAnalyzing(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('ai-quality-scorer', {
        body: {
          content: content,
          contentType: contentType,
          userId: session?.user?.id
        }
      });

      if (error) throw error;
      
      setScore(data.score);
      refetchAnalyses(); // Refresh history
      
      toast({
        title: "Analysis Complete",
        description: "Content quality has been analyzed successfully.",
      });
    } catch (error) {
      console.error('Error analyzing content:', error);
      toast({
        title: "Analysis Failed",
        description: "Failed to analyze content quality. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const loadSampleContent = () => {
    const samples = {
      mcq: `Which of the following is the primary pacemaker of the heart?
A) Atrioventricular (AV) node
B) Sinoatrial (SA) node
C) Bundle of His
D) Purkinje fibers

Correct Answer: B) Sinoatrial (SA) node

Explanation: The sinoatrial (SA) node, located in the right atrium, serves as the heart's natural pacemaker. It generates electrical impulses that initiate each heartbeat and set the heart rate under normal circumstances.`,
      note: `# Cardiovascular System Overview

## Heart Structure
The heart is a four-chambered muscular organ that pumps blood throughout the body. It consists of:
- Right atrium and ventricle (pulmonary circulation)
- Left atrium and ventricle (systemic circulation)

## Cardiac Cycle
The cardiac cycle includes:
1. Diastole (relaxation phase)
2. Systole (contraction phase)

Blood flows from atria to ventricles during diastole, then from ventricles to arteries during systole.`
    };
    
    setContent(samples[contentType as keyof typeof samples]);
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return "text-success";
    if (score >= 80) return "text-primary";
    if (score >= 70) return "text-warning";
    return "text-destructive";
  };

  const getScoreLabel = (score: number) => {
    if (score >= 90) return "Excellent";
    if (score >= 80) return "Good";
    if (score >= 70) return "Fair";
    return "Needs Improvement";
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "improved": return "success";
      case "reviewed": return "default";
      case "pending": return "secondary";
      case "dismissed": return "outline";
      default: return "secondary";
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Content Quality Scorer</h3>
          <p className="text-sm text-muted-foreground">
            AI-powered analysis of content quality with detailed scoring and suggestions
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Content Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Content Type</label>
                  <Select value={contentType} onValueChange={setContentType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mcq">MCQ Question</SelectItem>
                      <SelectItem value="note">Study Note</SelectItem>
                      <SelectItem value="explanation">Explanation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button variant="outline" onClick={loadSampleContent} className="w-full">
                    Load Sample Content
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Content to Analyze</label>
                <Textarea
                  placeholder="Enter your content here for quality analysis..."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={12}
                  className="resize-none"
                />
              </div>

              <Button 
                onClick={analyzeContent} 
                disabled={isAnalyzing || !content.trim()}
                className="w-full flex items-center gap-2"
              >
                <Brain className="h-4 w-4" />
                {isAnalyzing ? "Analyzing..." : "Analyze Quality"}
              </Button>
            </CardContent>
          </Card>

          {score && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Quality Analysis Results
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center">
                  <div className={`text-4xl font-bold ${getScoreColor(score.overall)}`}>
                    {score.overall}%
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Overall Quality Score - {getScoreLabel(score.overall)}
                  </p>
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Clarity</span>
                        <span className={getScoreColor(score.clarity)}>{score.clarity}%</span>
                      </div>
                      <Progress value={score.clarity} className="h-2" />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Accuracy</span>
                        <span className={getScoreColor(score.accuracy)}>{score.accuracy}%</span>
                      </div>
                      <Progress value={score.accuracy} className="h-2" />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Completeness</span>
                        <span className={getScoreColor(score.completeness)}>{score.completeness}%</span>
                      </div>
                      <Progress value={score.completeness} className="h-2" />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Relevance</span>
                        <span className={getScoreColor(score.relevance)}>{score.relevance}%</span>
                      </div>
                      <Progress value={score.relevance} className="h-2" />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Structure</span>
                        <span className={getScoreColor(score.structure)}>{score.structure}%</span>
                      </div>
                      <Progress value={score.structure} className="h-2" />
                    </div>
                  </div>
                </div>

                {score.suggestions.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm text-success">Suggestions for Improvement</h4>
                    <ul className="space-y-1">
                      {score.suggestions.map((suggestion, index) => (
                        <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                          <CheckCircle className="h-3 w-3 text-success mt-0.5 flex-shrink-0" />
                          {suggestion}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {score.issues.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm text-destructive">Issues Found</h4>
                    <ul className="space-y-1">
                      {score.issues.map((issue, index) => (
                        <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                          <AlertTriangle className="h-3 w-3 text-destructive mt-0.5 flex-shrink-0" />
                          {issue}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Recent Analyses
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentAnalyses && recentAnalyses.length > 0 ? (
                recentAnalyses.map((analysis) => (
                  <div key={analysis.id} className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0">
                        <Badge variant="outline" className="mb-1">{analysis.content_type}</Badge>
                        <p className="text-xs text-muted-foreground">
                          {new Date(analysis.analyzed_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge variant={getStatusColor(analysis.status) as any} className="ml-2">
                        {analysis.overall_score}%
                      </Badge>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No analyses yet
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
